// /lib/chart-helpers/custom-overlays/specific-trade-panel/orderSlTpOverlay.ts

import { klinecharts } from "../../klinecharts-client";

export interface OrderSlTpData {
  price: number;
  type: "SL" | "TP";
  onPriceChange?: (type: string, price: number) => void;
}

// Function to get color based on SL/TP type (same as interactive overlay)
const getSlTpColor = (type: string): string => {
  return type === "SL" ? "#F44336" : "#4CAF50"; // Red for SL, Green for TP
};

const getDraggedPrice = (event: any): number | null => {
  const pointValue = event?.overlay?.points?.[0]?.value;
  if (Number.isFinite(pointValue)) {
    return pointValue;
  }

  if (event?.yAxis && event?.y !== undefined) {
    const axisValue = event.yAxis.convertFromPixel(event.y);
    return Number.isFinite(axisValue) ? axisValue : null;
  }

  return null;
};

// Function to create visual elements for SL/TP lines
const createOrderSlTpFigures = (params: any) => {
  const { coordinates, bounding, styles, overlay, precision } = params;

  if (coordinates.length < 1) return [];

  const figures = [];
  const startPoint = coordinates[0];
  const slTpData: OrderSlTpData = overlay.extendData;

  if (!slTpData) return [];

  const lineColor = getSlTpColor(slTpData.type);

  // 1. Horizontal DASHED line across the chart
  const lineStartX = 0;
  const lineEndX = bounding.width;

  const slTpLine = {
    key: "orderSlTpLine",
    type: "line",
    attrs: {
      coordinates: [
        { x: lineStartX, y: startPoint.y },
        { x: lineEndX, y: startPoint.y },
      ],
    },
    styles: {
      style: "dashed", // DASHED line like in interactive overlay
      color: lineColor,
      size: 2,
      dashedValue: [8, 4], // Same dash pattern as interactive
    },
  };
  figures.push(slTpLine);

  return figures;
};

export const orderSlTpOverlay = {
  name: "customOrderSlTp",
  totalStep: 1,
  lock: false, // Allow dragging
  visible: true,
  zLevel: 7, // Below order lines
  needDefaultPointFigure: false,
  needDefaultXAxisFigure: false,
  needDefaultYAxisFigure: true, // Show price label on Y axis
  mode: "normal" as const,
  modeSensitivity: 8,

  createPointFigures: (params: any) => {
    return createOrderSlTpFigures(params);
  },

  // Price label on Y axis - same style as interactive overlay
  createYAxisFigures: (params: any) => {
    const { coordinates, overlay, yAxis } = params;

    if (coordinates.length < 1) return [];

    const slTpData: OrderSlTpData = overlay.extendData;
    if (!slTpData) return [];

    const lineColor = getSlTpColor(slTpData.type);
    const yCoord = coordinates[0].y;
    const currentPrice = yAxis.convertFromPixel(yCoord);
    const priceText = `${slTpData.type} ${currentPrice.toFixed(5)}`;

    return [
      {
        key: "yAxisOrderSlTpLabel",
        type: "text",
        attrs: {
          x: 0,
          y: yCoord,
          text: priceText,
          align: "left",
          baseline: "middle",
        },
        styles: {
          style: "fill",
          color: "#FFFFFF",
          size: 11,
          family: "Arial",
          weight: "bold",
          backgroundColor: lineColor,
          paddingLeft: 6,
          paddingRight: 6,
          paddingTop: 3,
          paddingBottom: 3,
          borderRadius: 3,
          borderStyle: "solid",
          borderColor: lineColor,
          borderSize: 1,
        },
      },
    ];
  },

  // Event handlers for dragging
  onClick: () => false,
  onDoubleClick: () => false,
  onPressedMoveStart: () => false, // Allow dragging to start
  onPressedMoving: (event: any) => {
    const { overlay } = event;
    const newPrice = getDraggedPrice(event);
    if (overlay?.extendData && newPrice !== null) {
      overlay.extendData.price = newPrice;
    }
    return false; // Continue dragging
  },
  onPressedMoveEnd: (event: any) => {
    const { overlay } = event;
    const finalPrice = getDraggedPrice(event);
    if (overlay?.extendData && finalPrice !== null) {
      overlay.extendData.price = finalPrice;

      const onPriceChange =
        overlay.onPriceChange || overlay.extendData.onPriceChange;
      onPriceChange?.(overlay.extendData.type, finalPrice);
    }
    return false; // Finish dragging
  },
};

// Function to register overlay
export const registerOrderSlTpOverlay = () => {
  try {
    klinecharts.registerOverlay(orderSlTpOverlay);
  } catch (error) {
    console.error("Failed to register order SL/TP overlay:", error);
  }
};

// Helper function to create order SL/TP overlay
export const createOrderSlTpOverlay = (
  chartInstance: any,
  slTpData: OrderSlTpData,
  onPriceChange?: (type: string, price: number) => void
) => {
  if (!chartInstance) return null;

  // Get proper timestamp from chart data instead of Date.now()
  const dataList = chartInstance.getDataList();
  const timestamp =
    dataList && dataList.length > 0
      ? dataList[dataList.length - 1].timestamp
      : Date.now();

  const overlayConfig = {
    name: "customOrderSlTp",
    lock: false,
    points: [
      {
        timestamp: timestamp, // Use chart timestamp instead of Date.now()
        value: slTpData.price,
      },
    ],
    extendData: {
      ...slTpData,
      onPriceChange,
    },
  };

  const overlayId = chartInstance.createOverlay(overlayConfig);

  if (overlayId && onPriceChange) {
    const overlay = chartInstance.getOverlayById(overlayId);
    if (overlay) {
      overlay.onPriceChange = onPriceChange;
    }
  }

  return overlayId;
};
